// src/components/Analytics/AnalyticsTracker.js

import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { AnalyticsService } from '../../apiService';

// ⚠️ VARIABLES GLOBALES EN DEHORS DU COMPOSANT
// Empêche React de les recréer à chaque render
let GLOBAL_SESSION_ID = null;
let GLOBAL_VISITOR_ID = null;
let GLOBAL_LOCATION_DATA = null;
let CURRENT_TRACKED_PAGE = null;
let IS_TRACKING = false;

/**
 * Composant Analytics Tracker - VERSION VERROUILLÉE
 * Garantit 1 SEUL track par page, JAMAIS plus
 */
const AnalyticsTracker = () => {
  const location = useLocation();

  /**
   * Générer un ID unique
   */
  const generateId = () => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  };

  /**
   * Initialiser les IDs (une seule fois dans toute l'app)
   */
  const initializeIds = () => {
    if (!GLOBAL_VISITOR_ID) {
      let visitorId = localStorage.getItem('analytics_visitor_id');
      if (!visitorId) {
        visitorId = generateId();
        localStorage.setItem('analytics_visitor_id', visitorId);
      }
      GLOBAL_VISITOR_ID = visitorId;
    }

    if (!GLOBAL_SESSION_ID) {
      let sessionId = sessionStorage.getItem('analytics_session_id');
      let sessionStart = sessionStorage.getItem('analytics_session_start');

      if (!sessionId) {
        sessionId = generateId();
        sessionStart = Date.now().toString();
        sessionStorage.setItem('analytics_session_id', sessionId);
        sessionStorage.setItem('analytics_session_start', sessionStart);
      }

      GLOBAL_SESSION_ID = sessionId;
    }
  };

  /**
   * Obtenir la géolocalisation (avec cache global)
   */
  const getGeolocation = async () => {
    const cached = sessionStorage.getItem('analytics_location');
    const cacheTime = sessionStorage.getItem('analytics_location_time');

    if (cached && cacheTime) {
      const age = Date.now() - parseInt(cacheTime);
      if (age < 3600000) {
        return JSON.parse(cached);
      }
    }

    return new Promise((resolve) => {
      if (!navigator.geolocation) {
        resolve(null);
        return;
      }

      const timeoutId = setTimeout(() => resolve(null), 2000);

      navigator.geolocation.getCurrentPosition(
        (position) => {
          clearTimeout(timeoutId);
          const data = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          };

          sessionStorage.setItem('analytics_location', JSON.stringify(data));
          sessionStorage.setItem('analytics_location_time', Date.now().toString());

          resolve(data);
        },
        () => {
          clearTimeout(timeoutId);
          resolve(null);
        },
        {
          enableHighAccuracy: false,
          timeout: 2000,
          maximumAge: 3600000,
        }
      );
    });
  };

  /**
   * Obtenir le pays via IP (avec cache global)
   */
  const getCountryFromIP = async () => {
    const cached = sessionStorage.getItem('analytics_country');
    if (cached) {
      return JSON.parse(cached);
    }

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 2000);

      const response = await fetch('https://ipapi.co/json/', {
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (response.ok) {
        const data = await response.json();
        const countryData = {
          country: data.country_name,
          country_code: data.country_code,
          city: data.city,
        };

        sessionStorage.setItem('analytics_country', JSON.stringify(countryData));
        return countryData;
      }
    } catch (error) {
      // Silencieux
    }
    return null;
  };

  /**
   * Collecter les données de géolocalisation
   */
  const collectLocationData = async () => {
    if (GLOBAL_LOCATION_DATA) {
      return GLOBAL_LOCATION_DATA;
    }

    const [geoLocation, ipLocation] = await Promise.all([
      getGeolocation(),
      getCountryFromIP(),
    ]);

    GLOBAL_LOCATION_DATA = {
      latitude: geoLocation?.latitude || null,
      longitude: geoLocation?.longitude || null,
      country: ipLocation?.country || null,
      country_code: ipLocation?.country_code || null,
      city: ipLocation?.city || null,
    };

    return GLOBAL_LOCATION_DATA;
  };

  /**
   * Extraire l'ID d'article de l'URL
   */
  const getArticleIdFromUrl = () => {
    const match = location.pathname.match(/\/news\/(\d+)/);
    return match ? parseInt(match[1]) : null;
  };

  /**
   * Envoyer l'événement analytics - ULTRA VERROUILLÉ
   */
  const trackPageView = async () => {
    const pageKey = location.pathname + location.search;

    // ✅ PROTECTION ABSOLUE 1 : Cette page a déjà été trackée ?
    if (CURRENT_TRACKED_PAGE === pageKey) {
      console.log('🚫 BLOCKED: Page already tracked:', pageKey);
      return;
    }

    // ✅ PROTECTION ABSOLUE 2 : Un tracking est en cours ?
    if (IS_TRACKING) {
      console.log('🚫 BLOCKED: Tracking already in progress');
      return;
    }

    try {
      IS_TRACKING = true;
      console.log('🔒 LOCK: Starting track for:', pageKey);

      const locationData = await collectLocationData();
      const sessionStart = parseInt(sessionStorage.getItem('analytics_session_start'));
      const now = Date.now();

      const eventData = {
        session_id: GLOBAL_SESSION_ID,
        visitor_id: GLOBAL_VISITOR_ID,
        page_url: window.location.href,
        page_title: document.title,
        article_id: getArticleIdFromUrl(),
        event_type: 'page_view',
        user_agent: navigator.userAgent,
        referrer: document.referrer || null,
        session_duration: Math.floor((now - sessionStart) / 1000),
        page_view_duration: 0,
        ...locationData,
      };

      await AnalyticsService.track(eventData);

      // ✅ Marquer cette page comme trackée SEULEMENT après succès
      CURRENT_TRACKED_PAGE = pageKey;
      console.log('✅ SUCCESS: Analytics tracked for:', pageKey);

    } catch (error) {
      console.error('❌ ERROR: Analytics failed:', error.message);
      // Ne pas marquer comme tracké en cas d'erreur
    } finally {
      IS_TRACKING = false;
      console.log('🔓 UNLOCK: Tracking complete');
    }
  };

  /**
   * Effet principal - Se déclenche au changement de page
   */
  useEffect(() => {
    // Initialiser les IDs au premier montage
    initializeIds();

    // Reset pour la nouvelle page
    const pageKey = location.pathname + location.search;

    // Si c'est une nouvelle page, autoriser le tracking
    if (CURRENT_TRACKED_PAGE !== pageKey) {
      CURRENT_TRACKED_PAGE = null;

      // Debounce de 500ms pour être sûr que la page est chargée
      const timeoutId = setTimeout(() => {
        trackPageView();
      }, 500);

      return () => {
        clearTimeout(timeoutId);
      };
    }
  }, [location.pathname, location.search]);

  return null;
};

export default AnalyticsTracker;
